import { useNavigate } from "react-router-dom";
import { STORAGE } from "./const";
import { UserRole } from "@/shared/utils/enum";
import { StorageI } from "@/types/interfaces";
import { toast } from "@/utils/appToast";
import i18n from "@/i18n";

// Constants for storage keys
const AUTH_FLAG = 'isAuthenticated';
const STORAGE_TYPE = 'storageType'; // 'local' or 'session'
const LANGUAGE_KEY = 'appLanguage';
const isBrowser = typeof window !== "undefined";

//remember=true for storing in localstorage else sessionStorage will be used
export const SetStorage = (data: StorageI, remember: boolean = true) => {
  if (!isBrowser) return;
  if (remember) {
    localStorage.setItem(STORAGE, JSON.stringify(data));
    localStorage.setItem(STORAGE_TYPE, 'local');
  } else {
    sessionStorage.setItem(STORAGE, JSON.stringify(data));
    sessionStorage.setItem(STORAGE_TYPE, 'session');
  }
};

export const GetStorage = () => {
  if (!isBrowser) return null;
  const savedCredentials =
    sessionStorage.getItem(STORAGE) || localStorage.getItem(STORAGE);
  let credentials: StorageI | null = null;
  if (savedCredentials) {
    credentials = JSON.parse(savedCredentials);
  }
  return credentials;
};

export const Logout = async (isShowToast: boolean=true) => {
  if (!isBrowser) return true;
  
  // Clear both storages to be safe
  sessionStorage.clear();
  localStorage.clear();
  
  if(isShowToast){
    toast.success(i18n.t("toastLogoutSuccess"));
  }
  return await true;
};

// Set authentication state (true/false) with remember me support
export const setAuthentication = (isAuth: boolean, remember: boolean = true) => {
  if (!isBrowser) return;
  if (isAuth) {
    if (remember) {
      localStorage.setItem(AUTH_FLAG, 'true');
      localStorage.setItem(STORAGE_TYPE, 'local');
    } else {
      sessionStorage.setItem(AUTH_FLAG, 'true');
      sessionStorage.setItem(STORAGE_TYPE, 'session');
    }
  } else {
    // Clear from both storages when setting to false
    localStorage.removeItem(AUTH_FLAG);
    sessionStorage.removeItem(AUTH_FLAG);
    localStorage.removeItem(STORAGE_TYPE);
    sessionStorage.removeItem(STORAGE_TYPE);
  }
};

// Check authentication state with proper storage type detection
export const isAuthenticated = (): boolean => {
  if (!isBrowser) return false;
  // Check session storage first, then local storage
  const sessionAuth = sessionStorage.getItem(AUTH_FLAG);
  const localAuth = localStorage.getItem(AUTH_FLAG);
  
  return sessionAuth === 'true' || localAuth === 'true';
};

// This is needed if Session id exists in the storage. 
export const IsAuthenticated = (): boolean => {
  if (!isBrowser) return false;
  const savedCredentials =
    sessionStorage.getItem(STORAGE) || localStorage.getItem(STORAGE);
  let credentials: StorageI | null = null;
  if (savedCredentials) {
    credentials = JSON.parse(savedCredentials);
  }
  // Backward compatibility: check both credentials and isAuthenticated flag
  return !!credentials?.data || isAuthenticated();
};

export const IsUser = (): boolean => {
  if (!isBrowser) return false;
  const savedCredentials =
    sessionStorage.getItem(STORAGE) || localStorage.getItem(STORAGE);
  let credentials: StorageI | null = null;
  if (savedCredentials) {
    credentials = JSON.parse(savedCredentials);
  }
  return credentials?.data?.user?.type===UserRole.USER.toLowerCase();
};

export const IsWorker = (): boolean => {
  if (!isBrowser) return false;
  const savedCredentials =
    sessionStorage.getItem(STORAGE) || localStorage.getItem(STORAGE);
  let credentials: StorageI | null = null;
  if (savedCredentials) {
    credentials = JSON.parse(savedCredentials);
  }
  return credentials?.data?.user?.type===UserRole.WORKER.toLowerCase();
};

export const IsAdmin = (): boolean => {
  if (!isBrowser) return false;
  const savedCredentials =
    sessionStorage.getItem(STORAGE) || localStorage.getItem(STORAGE);
  let credentials: StorageI | null = null;
  if (savedCredentials) {
    credentials = JSON.parse(savedCredentials);
  }
  return credentials?.data.role===UserRole.ADMIN;
};

// Helper function to get the current storage type
export const getStorageType = (): 'local' | 'session' | null => {
  if (!isBrowser) return null;
  const localType = localStorage.getItem(STORAGE_TYPE);
  const sessionType = sessionStorage.getItem(STORAGE_TYPE);
  
  if (sessionType === 'session') return 'session';
  if (localType === 'local') return 'local';
  return null;
};

export const SetLanguage = (lang: string, remember: boolean = true) => {
  if (!isBrowser) return;
  if (remember) {
    localStorage.setItem(LANGUAGE_KEY, lang);
  } else {
    sessionStorage.setItem(LANGUAGE_KEY, lang);
  }
}
export const GetLanguage = (): string | null => {
  if (!isBrowser) return null;
  return sessionStorage.getItem(LANGUAGE_KEY) || localStorage.getItem(LANGUAGE_KEY);
}

export default { 
  SetStorage, 
  GetStorage, 
  IsAuthenticated, 
  IsUser, 
  IsAdmin, 
  Logout, 
  setAuthentication, 
  isAuthenticated, 
  getStorageType 
};
